import React, { useState, useEffect } from 'react';
import { Zap, Leaf, Layers, Share2, Award, BarChart2, Wind, Database } from 'lucide-react';

export default function Agri() {
  const [visibleSections, setVisibleSections] = useState([]);

  const sections = [
    {
      id: "outcome",
      title: "Outcome",
      icon: <Award size={36} className="text-blue-600" />,
      content: [
        { icon: <Zap size={20} />, text: "Revolutionize farming technologies and improve agricultural output." },
        { icon: <BarChart2 size={20} />, text: "Enhance export capabilities and global market competitiveness." },
        { icon: <Wind size={20} />, text: "Position India as a \"Beyond Rice Bowl\" destination in India." }
      ],
      video: "/videos/outcome.mp4"
    }
  ];

  useEffect(() => {
    const observerOptions = { root: null, rootMargin: '0px', threshold: 0.25 };
    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setVisibleSections(prev => {
            if (!prev.includes(entry.target.id)) {
              return [...prev, entry.target.id];
            }
            return prev;
          });
        }
      });
    };
    const observer = new IntersectionObserver(observerCallback, observerOptions);
    sections.forEach(section => {
      const element = document.getElementById(section.id);
      if (element) observer.observe(element);
    });
    return () => {
      sections.forEach(section => {
        const element = document.getElementById(section.id);
        if (element) observer.unobserve(element);
      });
    };
  }, []);

  const isVisible = (sectionId) => visibleSections.includes(sectionId);

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Page Heading */}
      <div className="pt-6 pb-2 text-center">
        <h1 className="text-4xl font-bold text-green-700">
          Agriculture & Energy Innovation
        </h1>
        <p className="mt-2 text-gray-600 text-lg max-w-2xl mx-auto">
          Discover Dutch collaborations, smart irrigation, vertical farming, and sustainable agri innovations.
        </p>
      </div>

      {/* Agriculture and Agri-tech Section */}
      <div id="agritech" className="min-h-[80vh] flex items-center justify-center py-4 px-4">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">

          {/* Video */}
          <div className="overflow-hidden rounded-xl shadow-xl">
            <video autoPlay loop muted playsInline className="w-full h-full object-cover">
              <source src="/videos/agri.mp4" type="video/mp4" />
            </video>
          </div>

          {/* Content */}
          <div>
            <div className="flex items-center mb-6">
              <div className="text-green-600">
                <Leaf size={36} />
              </div>
              <h2 className="text-3xl font-bold ml-4">Agriculture and Agri-tech</h2>
            </div>
            <div className="space-y-6">
              {[
                {
                  icon: <Layers size={20} />,
                  text: "Leverage Dutch expertise in precision agriculture, vertical farming, and sustainable irrigation technologies to enhance agricultural productivity in India."
                },
                {
                  icon: <Database size={20} />,
                  text: "Collaborate on cold storage solutions and supply chain infrastructure to minimize post-harvest losses and improve food security."
                },
                {
                  icon: <Share2 size={20} />,
                  text: "Foster joint research and development (R&D) initiatives to develop drought-resistant crop varieties tailored to the region's climatic conditions."
                }
              ].map((item, index) => (
                <div key={index} className="flex items-start p-4 rounded-lg border border-gray-100 bg-white hover:border-green-200 hover:bg-green-50 transition-all duration-300 shadow-md">
                  <div className="bg-green-100 p-3 rounded-full mr-4 text-green-600">
                    {item.icon}
                  </div>
                  <p className="text-gray-700">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Outcome Section */}
      {sections.map((section) => (
        <div
          key={section.id}
          id={section.id}
          className="min-h-[80vh] flex items-center justify-center py-4 px-4"
        >
          <div className={`max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-6 items-center transition-all duration-1000 transform ${
            isVisible(section.id) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}>

            {/* Video */}
            <div className="overflow-hidden rounded-xl shadow-xl">
              <video autoPlay loop muted playsInline className="w-full h-full object-cover">
                <source src={section.video} type="video/mp4" />
              </video>
            </div>

            {/* Content */}
            <div>
              <div className="flex items-center mb-6">
                <div className="text-blue-600">
                  {section.icon}
                </div>
                <h2 className="text-3xl font-bold ml-4">{section.title}</h2>
              </div>

              <div className="space-y-6">
                {section.content.map((item, index) => (
                  <div
                    key={index}
                    className={`flex items-start p-4 rounded-lg border border-gray-100 bg-white hover:border-blue-200 hover:bg-blue-50 transition-all duration-300 shadow-md transform ${
                      isVisible(section.id) ? `opacity-100 translate-x-0 delay-${index * 2}00` : 'opacity-0 -translate-x-8'
                    }`}
                  >
                    <div className="bg-blue-100 p-3 rounded-full mr-4 text-blue-600">
                      {item.icon}
                    </div>
                    <p className="text-gray-700">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      ))}
    </div>
  );
}
